import _ from 'lodash'
import cookies from 'js-cookie'
import { starterPackTemplates } from './viewPresetTemplates'

export default {
  state: {
    userViewPresets: [],
    stationViewPresets: [],
    currentPresetName: 'Default',
    initialized: false,
    userPreferencesLoaded: false,
    stationPreferencesLoaded: false,
    starterPack: [...starterPackTemplates],
    viewPresetDefaults: [{ name: 'My Stories', quickFilter: '%CurrentUser%', saveOptions: ['filter'] }]
  },
  mutations: {
    SET_USER_VIEW_PRESETS(state, presets) {
      state.userViewPresets = Array.isArray(presets) ? presets.filter((p) => p.name) : []
    },
    SET_STATION_VIEW_PRESETS(state, presets) {
      state.stationViewPresets = Array.isArray(presets) ? presets.filter((p) => p.name) : []
    },
    SET_CURRENT_PRESET_NAME(state, name) {
      state.currentPresetName = name || 'Default'
    },
    SET_VIEW_PRESETS_INITIALIZED(state, initialized) {
      state.initialized = initialized
    },
    SET_USER_PREFERENCES_LOADED(state, loaded) {
      state.userPreferencesLoaded = loaded
    },
    SET_STATION_PREFERENCES_LOADED(state, loaded) {
      state.stationPreferencesLoaded = loaded
    },
    ADD_USER_VIEW_PRESET(state, preset) {
      const index = state.userViewPresets.findIndex((p) => p.name === preset.name)
      if (index > -1) {
        state.userViewPresets[index] = preset
      } else {
        state.userViewPresets.push(preset)
      }
    },
    ADD_STATION_VIEW_PRESET(state, preset) {
      const index = state.stationViewPresets.findIndex((p) => p.name === preset.name)
      if (index > -1) {
        state.stationViewPresets[index] = preset
      } else {
        state.stationViewPresets.push(preset)
      }
    },
    DELETE_USER_VIEW_PRESET(state, presetName) {
      const index = state.userViewPresets.findIndex((p) => p.name === presetName)
      if (index > -1) {
        state.userViewPresets.splice(index, 1)
      }
    },
    DELETE_STATION_VIEW_PRESET(state, presetName) {
      const index = state.stationViewPresets.findIndex((p) => p.name === presetName)
      if (index > -1) {
        state.stationViewPresets.splice(index, 1)
      }
    },
    ENSURE_DEFAULT_PRESET(state, defaultPreset) {
      if (!state.userViewPresets.find((p) => p.name === 'Default')) {
        if (defaultPreset) {
          state.userViewPresets.push(defaultPreset)
        }
        // If no default preset is provided, the grid will use its default column definitions
      }
    }
  },
  actions: {
    // Called by user.js when user preferences are loaded
    notifyUserPreferencesLoaded({ commit, dispatch, state, rootGetters }) {
      commit('SET_USER_PREFERENCES_LOADED', true)

      // Set user presets
      const userPreferences = rootGetters.preferences
      const userViewPresets = userPreferences?.viewPresets || []
      commit('SET_USER_VIEW_PRESETS', userViewPresets)

      // Try to initialize if both are ready
      dispatch('tryInitialize')
    },

    // Called by station.js when station preferences are loaded
    notifyStationPreferencesLoaded({ commit, dispatch, state, rootGetters }) {
      commit('SET_STATION_PREFERENCES_LOADED', true)

      // Set station presets
      const stationPreferences = rootGetters.stationPreferences
      const stationViewPresets = stationPreferences?.viewPresets || []
      commit('SET_STATION_VIEW_PRESETS', stationViewPresets)

      // Try to initialize if both are ready
      dispatch('tryInitialize')
    },

    // Central initialization - only runs once when both preferences are loaded
    tryInitialize({ commit, state, rootGetters }, { defaultPreset = null } = {}) {
      // Guard: only initialize once
      if (state.initialized) {
        console.log('View presets already initialized, skipping')
        return
      }

      // Guard: wait for both preferences to load
      if (!state.userPreferencesLoaded || !state.stationPreferencesLoaded) {
        console.log('Waiting for preferences to load...', {
          user: state.userPreferencesLoaded,
          station: state.stationPreferencesLoaded
        })
        return
      }

      console.log('Initializing view presets...')

      // Ensure default preset exists
      commit('ENSURE_DEFAULT_PRESET', defaultPreset)

      // Load current preset from cookie
      const currentStation = rootGetters.currentStation
      if (currentStation?.station) {
        let presetName = cookies.get(`viewPreset_${currentStation.station}`) ?? 'Default'

        // Check for query parameter override
        if (typeof window !== 'undefined' && window.location) {
          const urlParams = new URLSearchParams(window.location.search)
          if (urlParams.get('preset')) {
            presetName = urlParams.get('preset')
          }
        }

        commit('SET_CURRENT_PRESET_NAME', presetName)
        console.log('Loaded preset from cookie:', presetName)
      }

      // Mark as initialized
      commit('SET_VIEW_PRESETS_INITIALIZED', true)
      console.log('View presets initialized successfully')
    },

    // Legacy method for backwards compatibility - now does nothing
    async initializeViewPresets({ dispatch }, { basePresets = [], defaultPreset = null } = {}) {
      console.warn('initializeViewPresets() is deprecated - initialization now happens automatically')
      // Trigger initialization in case it hasn't happened yet
      dispatch('tryInitialize', { defaultPreset })
    },

    async saveViewPreset({ commit, dispatch, state, rootGetters }, presetData) {
      if (presetData.saveOptions.includes('isStationScope')) {
        // Save as station preset
        commit('ADD_STATION_VIEW_PRESET', presetData)
        await dispatch(
          'saveStationPreferences',
          {
            key: 'viewPresets',
            data: [...state.stationViewPresets]
          },
          { root: true }
        )
        // Re-apply after saveStationPreferences reloads preferences (GET may not return latest)
        commit('ADD_STATION_VIEW_PRESET', presetData)
      } else {
        // Save as user preset
        commit('ADD_USER_VIEW_PRESET', presetData)
        await dispatch(
          'saveUserPreferences',
          {
            key: 'viewPresets',
            data: [...state.userViewPresets]
          },
          { root: true }
        )
        // Re-apply after saveUserPreferences reloads preferences (GET may not return latest)
        commit('ADD_USER_VIEW_PRESET', presetData)
      }

      // Set as current preset and update cookie
      dispatch('setCurrentPreset', presetData.name)
    },

    async deleteViewPreset({ commit, dispatch, state, rootGetters }, { presetName, isStationScope }) {
      if (isStationScope) {
        // Delete station preset
        commit('DELETE_STATION_VIEW_PRESET', presetName)
        await dispatch(
          'saveStationPreferences',
          {
            key: 'viewPresets',
            data: _.cloneDeep(state.stationViewPresets)
          },
          { root: true }
        )
      } else {
        // Delete user preset
        commit('DELETE_USER_VIEW_PRESET', presetName)
        await dispatch(
          'saveUserPreferences',
          {
            key: 'viewPresets',
            data: _.cloneDeep(state.userViewPresets)
          },
          { root: true }
        )
      }

      // Reset current preset if deleted, and update cookie
      if (state.currentPresetName === presetName) {
        dispatch('setCurrentPreset', 'Default')
      }
    },

    async clearAllUserViewPresets({ commit, dispatch }) {
      commit('SET_USER_VIEW_PRESETS', [])
      await dispatch(
        'saveUserPreferences',
        {
          key: 'viewPresets',
          data: []
        },
        { root: true }
      )
      // Update current preset and cookie
      dispatch('setCurrentPreset', 'Default')
    },

    setCurrentPreset({ commit, rootGetters }, presetName) {
      console.log('Setting current preset to:', presetName)
      commit('SET_CURRENT_PRESET_NAME', presetName)

      // Always update cookie when preset changes
      const currentStation = rootGetters.currentStation
      if (currentStation?.station && presetName) {
        cookies.set(`viewPreset_${currentStation.station}`, presetName, { secure: true, sameSite: 'strict' })
        console.log('Saved preset to cookie:', presetName)
      }
    }
  },
  getters: {
    viewPresets(state) {
      const user = state.userViewPresets.map((p) => ({ ...p, type: 'user' })) || []
      const workspace = state.stationViewPresets.map((p) => ({ ...p, type: 'workspace' })) || []
      const starter = state.starterPack.map((p) => ({ ...p, type: 'starter' })) || []
      const presets = [...starter, ...workspace, ...user]
      return _.uniqBy(presets, 'name')
    },

    currentViewPreset(state, getters) {
      if (!state.initialized) return null

      const preset = getters.viewPresets.find((p) => p.name === state.currentPresetName)

      return preset || getters.viewPresets.find((p) => p.name === 'Default') || null
    },

    viewPresetsInitialized(state) {
      return state.initialized
    },

    currentPresetName(state) {
      return state.currentPresetName
    },

    userViewPresets(state) {
      return state.userViewPresets
    },

    stationViewPresets(state) {
      return state.stationViewPresets
    },

    viewPresetDefaults(state) {
      return state.viewPresetDefaults
    }
  }
}
